import React from 'react';
import { Bot, Users, CalendarCheck, Clock } from 'lucide-react';

export const usd = (n, digits = 0) =>
  n == null ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

export function HeadlineStrip({ report }) {
  const h = report.headline;
  const positive = (h.savings_usd || 0) >= 0;
  return (
    <section className="rounded-2xl bg-slate-950 text-slate-50 p-6 md:p-8 relative overflow-hidden" data-testid="leo-impact-headline">
      <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-emerald-500/15 blur-3xl" />
      <div className="absolute right-24 bottom-0 h-40 w-40 rounded-full bg-amber-400/10 blur-3xl" />
      <div className="grid gap-6 md:grid-cols-12 relative">
        <div className="md:col-span-5">
          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Net saving vs. a human SDR doing the same work</p>
          <p className={`text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-2 ${positive ? 'text-emerald-400' : 'text-amber-300'}`} data-testid="leo-impact-savings">
            {usd(h.savings_usd)}
          </p>
          <p className="text-sm text-slate-300 mt-3">
            {h.roi_multiple ? <><span className="font-semibold text-slate-50" data-testid="leo-impact-roi-multiple">{h.roi_multiple}×</span> lower cost than the equivalent human execution · </> : null}
            <span data-testid="leo-impact-fte">{h.sdr_fte_equivalent}</span> SDR FTE-equivalent of work executed
          </p>
          {h.revenue_won_usd > 0 && (
            <div className="mt-4 inline-flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-xl bg-white/5 border border-white/10 px-4 py-3" data-testid="leo-impact-revenue">
              <span className="text-xs uppercase tracking-wide text-slate-400">Revenue won this period</span>
              <span className="text-2xl font-bold text-emerald-400 tabular-nums" data-testid="leo-impact-revenue-value">{usd(h.revenue_won_usd)}</span>
              {h.revenue_roi_multiple ? (
                <span className="text-sm text-slate-300"><span className="font-semibold text-slate-50" data-testid="leo-impact-revenue-roi">{h.revenue_roi_multiple}×</span> return on LEO spend</span>
              ) : null}
            </div>
          )}
        </div>
        <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi icon={Clock} label="Human hours replaced" value={h.human_hours_replaced} testId="leo-impact-hours" />
          <Kpi icon={Bot} label="Live conversations" value={h.live_conversations} testId="leo-impact-conversations" />
          <Kpi icon={CalendarCheck} label="Meetings booked" value={h.meetings_booked} testId="leo-impact-meetings" />
          <Kpi icon={Users} label="Total touches" value={h.total_touches} testId="leo-impact-touches" />
        </div>
      </div>
    </section>
  );
}

function Kpi({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm" data-testid={testId}>
      <Icon className="h-4 w-4 text-amber-300" />
      <p className="text-2xl font-semibold mt-2 tabular-nums">{value ?? 0}</p>
      <p className="text-[11px] text-slate-400 mt-0.5">{label}</p>
    </div>
  );
}
